import * as React from 'react'

import { requestPost, requestPut } from '../../../modules/request/index'
import {
  defaultSnackbarTimeout,
  getPageTitle,
  Snackbar,
} from '../../Shared/index'
import useSnackbarMonitor from '../../Shared/hooks/useSnackbarMonitor';
import TeacherDangerZone from '../components/accounts/edit/teacher_danger_zone'
import TeacherEmailNotifications from '../components/accounts/edit/teacher_email_notifications'
import TeacherGeneralAccountInfo from '../components/accounts/edit/teacher_general'
import TeacherGradeLevels from '../components/accounts/edit/teacher_grade_levels'
import TeacherLinkedAccounts from '../components/accounts/edit/teacher_linked_accounts'
import TeacherPasswordAccountInfo from '../components/accounts/edit/update_password'
import AccessibilitySettings from '../components/accounts/edit/accessibility_settings'
import TeacherSubjectAreas from '../components/accounts/edit/teacher_subject_areas'
import TeacherStudentDashboardSettings from '../components/accounts/edit/teacher_student_dashboard_settings'

type AlternativeSchool = unknown

interface AccountInfo {
  clever_id: string | null
  email: string
  google_id: string | null
  id: number | string
  maximum_grade_level: number
  minimum_grade_level: number
  name: string
  notification_email_frequency: string
  school: string
  school_type: string
  send_newsletter: boolean
  show_students_exact_score: boolean
  subject_area_ids: number[]
  teacher_notification_settings: TeacherNotificationSettings
  time_zone: string
  role: string
}

type SubjectArea = {
  id: number
  name: string
}

type TeacherNotificationSettings = Record<string, unknown>

interface TeacherAccountProps {
  accountInfo: AccountInfo
  alternativeSchools: AlternativeSchool[]
  alternativeSchoolsNameMap: Record<string, string>
  classlinkId: string
  cleverLink: string
  providerJustSet: boolean
  postGoogleClassroomAssignments: boolean
  showDismissSchoolSelectionReminderCheckbox: boolean
  subjectAreas: SubjectArea[]
}

function gradeLevelToOption(
  gradeLevel: number | null
): { label: string; value: number } | null {
  return gradeLevel != null
    ? { label: String(gradeLevel), value: gradeLevel }
    : null
}

const EMAIL_NOTIFICATIONS = 'emailNotifications'
const GENERAL = 'general'
const GRADE_LEVEL = 'gradeLevel'
const PASSWORD = 'password'
const SUBJECT_AREAS = 'subjectAreas'
const STUDENT_DASHBOARD_SETTINGS = 'studentDashboardSettings'
const ACCESSIBILITY_SETTINGS = 'accessibilitySettings'

const TeacherAccount: FC<TeacherAccountProps> = ({
  accountInfo,
  alternativeSchools,
  alternativeSchoolsNameMap,
  cleverLink,
  providerJustSet,
  postGoogleClassroomAssignments,
  showDismissSchoolSelectionReminderCheckbox,
  subjectAreas,
}) => {
  const {
    clever_id: initialCleverId,
    classlink_id: initialClasslinkId,
    email: initialEmail,
    google_id: initialGoogleId,
    id,
    maximum_grade_level: initialMaximumGradeLevel,
    minimum_grade_level: initialMinimumGradeLevel,
    name: initialName,
    notification_email_frequency: initialNotificationEmailFrequency,
    school: initialSchool,
    school_type: initialSchoolType,
    send_newsletter: initialSendNewsletter,
    show_students_exact_score: initialShowStudentsExactScore,
    snackbar_duration: initialSnackbarDuration,
    subject_area_ids: initialSubjectAreaIds,
    teacher_notification_settings: initialTeacherNotificationSettings,
    time_zone: initialTimeZone,
  } = accountInfo

  const [activeSection, setActiveSection] = React.useState<string | null>(null)
  const [classlinkId, setClasslinkId] = React.useState<string | null>(initialClasslinkId)
  const [cleverId, setCleverId] = React.useState<string | null>(initialCleverId)
  const [email, setEmail] = React.useState(initialEmail)
  const [errors, setErrors] = React.useState<Record<string, unknown>>({})
  const [googleId, setGoogleId] = React.useState<string | null>(initialGoogleId)
  const [maximumGradeLevel, setMaximumGradeLevel] = React.useState<number>(initialMaximumGradeLevel)
  const [minimumGradeLevel, setMinimumGradeLevel] = React.useState<number>(initialMinimumGradeLevel)
  const [name, setName] = React.useState(initialName)
  const [notificationEmailFrequency, setNotificationEmailFrequency] = React.useState(initialNotificationEmailFrequency)
  const [postNotificationEmailFrequency, setPostNotificationEmailFrequency] = React.useState(initialNotificationEmailFrequency)
  const [school, setSchool] = React.useState(initialSchool)
  const [schoolType, setSchoolType] = React.useState(initialSchoolType)
  const [sendNewsletter, setSendNewsletter] = React.useState(initialSendNewsletter)
  const [postSendNewsletter, setPostSendNewsletter] = React.useState(initialSendNewsletter)
  const [selectedSubjectAreaIds, setSelectedSubjectAreaIds] = React.useState<number[]>(initialSubjectAreaIds)
  const [showStudentsExactScore, setShowStudentsExactScore] = React.useState(initialShowStudentsExactScore)
  const [snackbarDuration, setSnackbarDuration] = React.useState(initialSnackbarDuration || defaultSnackbarTimeout)
  const [snackbarCopy, setSnackbarCopy] = React.useState('')
  const [showSnackbar, setShowSnackbar] = React.useState(false)
  const [timesSubmitted, setTimesSubmitted] = React.useState(0)
  const [teacherNotificationSettings, setTeacherNotificationSettings] = React.useState<TeacherNotificationSettings>(initialTeacherNotificationSettings)
  const [tempTeacherNotificationSettings, setTempTeacherNotificationSettings,] = React.useState<TeacherNotificationSettings>(initialTeacherNotificationSettings)
  const [timeZone, setTimeZone] = React.useState(initialTimeZone)

  useSnackbarMonitor(showSnackbar, setShowSnackbar, snackbarDuration, true);

  React.useEffect(() => {
    document.title = getPageTitle('Home', 'My Account')
  }, [])

  React.useEffect(() => {
    if (!providerJustSet) { return }

    const copy = `${providerJustSet} linked`
    setSnackbarCopy(copy)
    showTransientSnackbar()
  }, [])

  function activateSection(section: string) {
    setActiveSection(section)
  }

  function deactivateSection(section: string) {
    if (activeSection === section) {
      setActiveSection(null)
      setErrors({})
    }
  }

  function deleteAccount() {
    requestPost(
      `${window.postbuildEnv.DEFAULT_URL}/teachers/clear_data/${id}`,
      {},
      () => {
        window.location.href = window.location.origin
      }
    )
  }

  function resetTeacherNotificationSection() {
    setPostNotificationEmailFrequency(notificationEmailFrequency)
    setPostSendNewsletter(sendNewsletter)
    setTempTeacherNotificationSettings(teacherNotificationSettings)
  }

  function showTransientSnackbar() {
    setShowSnackbar(true)
  }

  function updateNotificationSettings(data: unknown) {
    requestPost(
      '/api/v1/teacher_notification_settings/bulk_update',
      data,
      (body: { teacher_notification_settings: TeacherNotificationSettings }) => {
        setTeacherNotificationSettings(body.teacher_notification_settings)
        setTempTeacherNotificationSettings(body.teacher_notification_settings)
        setErrors({})
        setActiveSection(null)
      },
      (body: { errors: Record<string, unknown> }) => {
        setErrors(body.errors)
        setTimesSubmitted((t) => t + 1)
      }
    )
  }

  function updateTeacherInfo(
    data: unknown,
    copy: string
  ) {
    requestPut(
      '/teacher_infos',
      data,
      (body: {
        maximum_grade_level: number
        minimum_grade_level: number
        notification_email_frequency: string
        show_students_exact_score: boolean
        subject_area_ids: number[]
      }) => {
        setErrors({})
        setMaximumGradeLevel(body.maximum_grade_level)
        setMinimumGradeLevel(body.minimum_grade_level)
        setNotificationEmailFrequency(body.notification_email_frequency)
        setPostNotificationEmailFrequency(body.notification_email_frequency)
        setSelectedSubjectAreaIds(body.subject_area_ids)
        setShowStudentsExactScore(body.show_students_exact_score)

        setSnackbarCopy(copy)
        if (copy) showTransientSnackbar()
        setActiveSection(null)
      },
      (body: { errors: Record<string, unknown> }) => {
        setErrors(body.errors)
        setTimesSubmitted((t) => t + 1)
      }
    )
  }

  function updateAccessibilitySettings(
    data: unknown,
    copy: string
  ) {
    requestPut(
      '/accessibility_setting',
      data,
      (body: {
        snackbar_duration: number
      }) => {
        setErrors({})
        setSnackbarDuration(body.snackbar_duration)

        setSnackbarCopy(copy)
        if (copy) showTransientSnackbar()
        setActiveSection(null)
      },
      (body: { errors: Record<string, unknown> }) => {
        setErrors(body.errors)
        setTimesSubmitted((t) => t + 1)
      }
    )
  }

  function updateUser(
    data: unknown,
    url: string,
    copy: string,
    onSuccess: () => void|null
  ) {
    requestPut(
      `${window.postbuildEnv.DEFAULT_URL}${url}`,
      data,
      (body: {
        clever_id: string | null
        classlink_id: string | null
        email: string
        google_id: string | null
        name: string
        school: string
        school_type: string
        send_newsletter: boolean,
        time_zone: string
      }) => {
        setErrors({})
        setClasslinkId(body.classlink_id)
        setCleverId(body.clever_id)
        setEmail(body.email)
        setGoogleId(body.google_id)
        setName(body.name)
        setSchool(body.school)
        setSchoolType(body.school_type)
        setPostSendNewsletter(body.send_newsletter)
        setSendNewsletter(body.send_newsletter)
        setTimeZone(body.time_zone)

        setSnackbarCopy(copy)
        showTransientSnackbar()
        setActiveSection(null)

        if (onSuccess) onSuccess()
      },
      (body: { errors: Record<string, unknown> }) => {
        setErrors(body.errors)
        setTimesSubmitted((t) => t + 1)
      }
    )
  }

  function handleChangeTeacherNotificationOption(key, value) {
    if (key === 'postNotificationEmailFrequency') {
      setPostNotificationEmailFrequency(value as string)
    }

    if (key === 'postSendNewsletter') {
      setPostSendNewsletter(value as boolean)
    }

    if (key === 'tempTeacherNotificationSettings') {
      setTempTeacherNotificationSettings(value as TeacherNotificationSettings)
    }
  }

  return (
    <main
      className="user-account white-background-accommodate-footer"
      id="post-secondary-nav"
    >
      <TeacherGeneralAccountInfo
        activateSection={() => activateSection(GENERAL)}
        active={activeSection === GENERAL}
        alternativeSchools={alternativeSchools}
        alternativeSchoolsNameMap={alternativeSchoolsNameMap}
        classlinkId={classlinkId}
        cleverId={cleverId}
        deactivateSection={() => deactivateSection(GENERAL)}
        email={email}
        errors={errors}
        googleId={googleId}
        name={name}
        school={school}
        schoolType={schoolType}
        showDismissSchoolSelectionReminderCheckbox={showDismissSchoolSelectionReminderCheckbox}
        timesSubmitted={timesSubmitted}
        timeZone={timeZone}
        updateUser={updateUser}
      />

      <TeacherPasswordAccountInfo
        activateSection={() => activateSection(PASSWORD)}
        active={activeSection === PASSWORD}
        cleverId={cleverId}
        deactivateSection={() => deactivateSection(PASSWORD)}
        errors={errors}
        googleId={googleId}
        role={accountInfo.role}
        timesSubmitted={timesSubmitted}
        updateUser={updateUser}
      />

      <TeacherLinkedAccounts
        classlinkId={classlinkId}
        cleverId={cleverId}
        cleverLink={cleverLink}
        email={email}
        errors={errors}
        googleId={googleId}
        onUserUpdateSuccess={() => (setClasslinkId(null))}
        postGoogleClassroomAssignments={postGoogleClassroomAssignments}
        timesSubmitted={timesSubmitted}
        updateUser={updateUser}

        user={accountInfo}
      />

      <TeacherEmailNotifications
        activateSection={() => activateSection(EMAIL_NOTIFICATIONS)}
        active={activeSection === EMAIL_NOTIFICATIONS}
        deactivateSection={() => deactivateSection(EMAIL_NOTIFICATIONS)}
        notificationEmailFrequency={postNotificationEmailFrequency}
        notificationSettings={tempTeacherNotificationSettings}
        resetTeacherNotificationSection={resetTeacherNotificationSection}
        sendNewsletter={postSendNewsletter}
        setTeacherNotificationOption={handleChangeTeacherNotificationOption}
        updateNotificationSettings={updateNotificationSettings}
        updateTeacherInfo={updateTeacherInfo}
      />

      <TeacherGradeLevels
        activateSection={() => activateSection(GRADE_LEVEL)}
        active={activeSection === GRADE_LEVEL}
        deactivateSection={() => deactivateSection(GRADE_LEVEL)}
        passedMaximumGradeLevel={gradeLevelToOption(maximumGradeLevel)}
        passedMinimumGradeLevel={gradeLevelToOption(minimumGradeLevel)}
        updateTeacherInfo={updateTeacherInfo}
      />

      <TeacherSubjectAreas
        activateSection={() => activateSection(SUBJECT_AREAS)}
        active={activeSection === SUBJECT_AREAS}
        deactivateSection={() => deactivateSection(SUBJECT_AREAS)}
        passedSelectedSubjectAreaIds={selectedSubjectAreaIds}
        subjectAreas={subjectAreas}
        updateTeacherInfo={updateTeacherInfo}
      />

      <TeacherStudentDashboardSettings
        activateSection={() => activateSection(STUDENT_DASHBOARD_SETTINGS)}
        active={activeSection === STUDENT_DASHBOARD_SETTINGS}
        deactivateSection={() => deactivateSection(STUDENT_DASHBOARD_SETTINGS)}
        passedShowStudentsExactScore={showStudentsExactScore}
        updateTeacherInfo={updateTeacherInfo}
      />

      <AccessibilitySettings
        activateSection={() => activateSection(ACCESSIBILITY_SETTINGS)}
        active={activeSection === ACCESSIBILITY_SETTINGS}
        deactivateSection={() => deactivateSection(ACCESSIBILITY_SETTINGS)}
        passedSnackbarDuration={snackbarDuration}
        updateAccessibilitySettings={updateAccessibilitySettings}
      />

      <TeacherDangerZone deleteAccount={deleteAccount} />

      <Snackbar text={snackbarCopy} visible={showSnackbar} />
    </main>
  )
}

export default TeacherAccount